import { Grid } from "@material-ui/core";
import {
  Send,
  Paperclip,
  X,
  Smile,
  File as FileIcon,
  Loader,
  Mic,
  Square,
} from "react-feather";
import styled from "styled-components/macro";
import styles from "../../communication-chat.module.css";
import TextareaAutosize from "react-textarea-autosize";
import { useRef, useState, useEffect } from "react";
import EmojiPicker from "emoji-picker-react";

const ChatInputsTwo = styled(Grid)`
  min-height: 40px;
  padding: 16px;
  position: relative;
`;

const MAX_RECORDING_SECONDS = 120; // 2 minutes

const IdentityChatInputBox = ({
  messageTextVal,
  handleChange,
  sendMessage,
  pendingAttachment,
  onSelectFile,
  onSelectRecordedAudio,
  onCancelAttachment,
  isUploading,
}) => {
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const [isEmojiOpen, setIsEmojiOpen] = useState(false);
  const emojiButtonRef = useRef(null);
  const emojiPopoverRef = useRef(null);

  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        isEmojiOpen &&
        emojiPopoverRef.current &&
        !emojiPopoverRef.current.contains(e.target) &&
        !emojiButtonRef.current?.contains(e.target)
      ) {
        setIsEmojiOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isEmojiOpen]);

  // Clean up mic stream and timer if the component unmounts mid-recording
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const handleEmojiClick = (emojiData) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      handleChange({ target: { value: messageTextVal + emojiData.emoji } });
      return;
    }
    const start = textarea.selectionStart ?? messageTextVal.length;
    const end = textarea.selectionEnd ?? messageTextVal.length;
    const newValue =
      messageTextVal.slice(0, start) +
      emojiData.emoji +
      messageTextVal.slice(end);
    handleChange({ target: { value: newValue } });
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + emojiData.emoji.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      onSelectRecordedAudio(
        null,
        "Voice recording isn't supported in this browser."
      );
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      recordedChunksRef.current = [];

      const mimeType = MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "audio/mp4";
      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) recordedChunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: mimeType });
        const file = new File(
          [blob],
          `voice-note-${Date.now()}.${
            mimeType.includes("webm") ? "webm" : "m4a"
          }`,
          {
            type: mimeType,
          }
        );
        onSelectRecordedAudio(file, null);
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      };

      recorder.start();
      setIsRecording(true);
      setRecordingSeconds(0);

      recordingTimerRef.current = setInterval(() => {
        setRecordingSeconds((prev) => {
          if (prev + 1 >= MAX_RECORDING_SECONDS) {
            stopRecording();
            return prev + 1;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (err) {
      console.log("getUserMedia failed", err);
      onSelectRecordedAudio(
        null,
        "Microphone access was denied. Please allow microphone access to record a voice note."
      );
    }
  };

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    setIsRecording(false);
  };

  const formatSeconds = (total) => {
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <div>
      {pendingAttachment && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "0 16px 8px",
          }}
        >
          {pendingAttachment.type.startsWith("image/") ? (
            <img
              src={pendingAttachment.previewUrl}
              alt=""
              style={{
                width: "48px",
                height: "48px",
                objectFit: "cover",
                borderRadius: "6px",
                marginRight: "8px",
              }}
            />
          ) : pendingAttachment.type.startsWith("audio/") ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginRight: "8px",
              }}
            >
              <Mic size={18} style={{ marginRight: "6px", color: "#502688" }} />
              <span style={{ fontSize: "13px" }}>Voice note</span>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginRight: "8px",
              }}
            >
              <FileIcon
                size={20}
                style={{ marginRight: "6px", color: "#502688" }}
              />
              <span style={{ fontSize: "13px" }}>{pendingAttachment.name}</span>
            </div>
          )}
          <button
            onClick={onCancelAttachment}
            style={{ background: "none", border: "none", cursor: "pointer" }}
          >
            <X size={16} />
          </button>
        </div>
      )}

      <ChatInputsTwo>
        {isEmojiOpen && (
          <div
            ref={emojiPopoverRef}
            style={{
              position: "absolute",
              bottom: "60px",
              left: "16px",
              zIndex: 10,
              boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
              borderRadius: "8px",
            }}
          >
            <EmojiPicker
              onEmojiClick={handleEmojiClick}
              height={350}
              width={300}
            />
          </div>
        )}

        {isRecording ? (
          <div
            className={`${styles.ChatInputSec}`}
            style={{ justifyContent: "space-between" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span
                style={{
                  width: "10px",
                  height: "10px",
                  borderRadius: "50%",
                  background: "#e23725",
                }}
              />
              <span style={{ fontSize: "14px", color: "#343434" }}>
                Recording… {formatSeconds(recordingSeconds)}
              </span>
            </div>
            <button
              onClick={stopRecording}
              className={`${styles.SendBUMSG}`}
              style={{ background: "#e23725", borderColor: "#e23725" }}
            >
              <Square size={18} />
            </button>
          </div>
        ) : (
          <div className={`${styles.ChatInputSec}`}>
            <button
              ref={emojiButtonRef}
              className={`${styles.AttatchFileBUMSG}`}
              onClick={() => setIsEmojiOpen((prev) => !prev)}
            >
              <Smile />
            </button>

            <button
              className={`${styles.AttatchFileBUMSG}`}
              onClick={() => fileInputRef.current?.click()}
            >
              <Paperclip />
            </button>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={(e) => {
                if (e.target.files?.[0]) onSelectFile(e.target.files[0]);
                e.target.value = "";
              }}
            />

            <button
              className={`${styles.AttatchFileBUMSG}`}
              onClick={startRecording}
              disabled={!!pendingAttachment}
              style={{ opacity: pendingAttachment ? 0.4 : 1 }}
            >
              <Mic />
            </button>

            <Grid item style={{ flexGrow: 1 }}>
              <TextareaAutosize
                ref={textareaRef}
                minRows={1}
                maxRows={3}
                placeholder="Type your message"
                value={messageTextVal}
                onChange={handleChange}
              />
            </Grid>

            <button
              onClick={sendMessage}
              className={`${styles.SendBUMSG}`}
              disabled={isUploading}
              style={{ opacity: isUploading ? 0.6 : 1 }}
            >
              {isUploading ? <Loader size={20} className="spin" /> : <Send />}
            </button>
          </div>
        )}
      </ChatInputsTwo>
    </div>
  );
};

export default IdentityChatInputBox;
